import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ILoopBackFilter } from '../shared/filter.interface';
import { ITEMS_PER_PAGE, OPERATOR } from './constants';
import { BaseHelperService } from '../services/helper/base.service';

export class Filter implements ILoopBackFilter {

  public fields?: any;
  public include?: any;
  public limit?: any;
  public order?: any;
  public skip?: any;
  public offset?: any;
  public where?: any;
  public page?: number = 1;
  public totalPages: number = 1;

  private _filters = {};

  /**
   * Constructor
   * @param filter
   */
  constructor(filter) {
    if (filter instanceof Filter) {
      return filter;
    } else if (typeof filter === 'undefined' || filter == null) {
      return this;
    } else {
      var self = this;
      var properties = Object.keys(filter);
      properties.forEach(function (property) {
        if (filter[property] != null) {
          self[property] = filter[property];
        }
      });
    }
  }

  /**
   * Add condition to filter
   * @param fieldName
   * @param fieldValue
   * @param options (operator, unionType, caseInsensitive, removeUnicode)
   */
  public addCriteria(fieldName, fieldValue, options) {
    let operator = options.operator || 'eq';
    let unionType = options.unionType || 'and';
    let caseInsensitive = options.caseInsensitive !== undefined ? options.caseInsensitive : false;
    let removeUnicode = options.removeUnicode !== undefined ? options.removeUnicode : true;
    if (removeUnicode) {
      fieldValue = BaseHelperService.convertToSearchString(fieldValue);
    }
    this._filters[fieldName+operator+unionType] = {
      name: fieldName,
      operator: operator,
      value: fieldValue,
      unionType: unionType,
      caseInsensitive: caseInsensitive
    };
  }

  /**
   * Add more condition to filter
   * @param conditions
   * @param unionType
   */
  public addConditions(conditions, unionType = 'and') {
    if (typeof this.where === 'undefined') {
      this.where = conditions;
    } else {
      // In case "where" not have both "and" and "or" operator
      if (typeof this.where.and === 'undefined' &&
        typeof this.where.or === 'undefined') {
        if (unionType == 'and') {
          this.where = {
            and: [this.where],
          };
        } else {
          this.where = {
            or: [this.where],
          };
        }
      }
      // Add new conditions
      if (unionType == 'and') {
        if (typeof this.where.and === 'undefined') {
          this.where = {
            and: [{
              or: this.where.or,
            }, conditions],
          };
        } else {
          this.where.and.push(conditions);
        }
      } else {
        if (typeof this.where.or === 'undefined') {
          this.where = {
            or: [{
              and: this.where.and,
            }, conditions],
          };
        } else {
          this.where.or.push(conditions);
        }
      }
    }
  }

  /**
   * Add more include to filter
   * @param include
   */
  public addInclude(include) {
    if (typeof include !== 'undefined' && include != null) {
      if (typeof this.include === 'undefined' || this.include == null) {
        this.include = include;
      } else {
        if (Array.isArray(this.include)) {
          this.include.push(include);
        } else if (typeof(this.include) === 'object') {
          this.include = [include, this.include];
        } else {
          this.include = [this.include, include];
        }
      }
    }
  }

  /**
   * Check an include exist or not
   * @param include
   */
  public existsInclude(include) {
    if (this.include) {
      if (Array.isArray(this.include)) {
        var blnExists = false;
        this.include.forEach(function(includeElement, index, arr) {
          if (typeof includeElement === 'string') {
            if (includeElement === include) {
              blnExists = true;
              return true;
            }
          } else if (typeof includeElement === 'object') {
            if (typeof includeElement.relation === include) {
              blnExists = true;
              return true;
            }
          }
        });
        return blnExists;
      } else {
        if (typeof this.include === 'string') {
          return (this.include === include);
        } else {
          if (typeof this.include.relation !== 'undefined') {
            return (this.include.relation === include);
          } else {
            return false;
          }
        }
      }
    } else {
      return false;
    }
  }

  /**
   * Generate like condition in filter
   * @param params
   * @param caseInsensitive
   */
  public addLike(field: any, text: any, caseInsensitive?: boolean, unionType: string = 'and') {
    let condition = {};
    condition[field] = {like: `.*${text}.*`};
    if (caseInsensitive) {
      condition[field] = {like: `.*${text}.*`, options: 'i'};
    }
    this.addConditions(condition, unionType);
  }

  /**
   * Add order to your filter
   * @param field
   * @param ascending
   */
  public addOrder(field: any, ascending: boolean = true) {
    let strOrder = field + ' ' + ascending ? 'ASC' : 'DESC';
    if (this.order == null) {
      this.order = strOrder;
    } else if (Array.isArray(this.order)) {
      this.order.push(strOrder);
    }
  }

  /**
   * Add field to your filter
   * @param field
   * @param include
   */
  public addField(field: any, include: boolean = true) {
    if (this.fields == null) {
      this.fields = {};
    }
    this.fields[field] = include;
  }

  /**
   * Reset filter
   */
  public reset() {
    this.fields = undefined;
    this.include = undefined;
    this.offset = undefined;
    this.where = undefined;
    this.order = undefined;
    this.skip = 0;
    this.limit = ITEMS_PER_PAGE;
  }

  /**
   * Update total pages for filter
   * @param count
   */
  public updateTotalPages(count: number = 1) {
    let pageSize = this.limit || ITEMS_PER_PAGE;
    this.totalPages = Math.ceil(count / pageSize);
  }

  /**
   * Update offset page for filter
   * @param pageNumber
   */
  public updatePageOffset(pageNumber) {
    let pageSize = this.limit || ITEMS_PER_PAGE;
    this.skip = (pageNumber - 1) * pageSize;
  }

  // Export filter to filter params
  public toString(): any {
    let self = this;
    let fieldNames = Object.keys(self._filters);
    fieldNames.forEach(function(fieldName) {
      let condition = self._filters[fieldName];
      switch (condition.operator) {
        case OPERATOR.LIKE:
          self.addLike(condition.name, condition.value, condition.caseInsensitive, condition.unionType);
        break;
        default:
          let criteria = {};
          criteria[condition.name] = condition.value;
          self.addConditions(criteria, condition.unionType);
        break;
      };
    });
    let filters = {};
    if (self.fields) {
      filters['fields'] = self.fields;
    }
    if (self.where) {
      filters['where'] = self.where;
    }
    if (self.include) {
      filters['include'] = self.include;
    }
    if (self.skip) {
      filters['skip'] = self.skip;
    }
    if (self.limit) {
      filters['limit'] = self.limit;
    }
    if (self.offset) {
      filters['offset'] = self.offset;
    }
    if (self.order) {
      filters['order'] = self.order;
    }
    this.reset();
    return filters;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/filter.ts